import { Tag, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import type { ActiveFaultCodeItem } from '@/service/vpc/types/IActiveStateLatestJson';
import type { IUnit } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';

import FormatDateTime from '@/components/FormatDateTime';
import { chassis } from '@/utils';

const useFaultCodesTable = (units: IUnit[] | undefined) => {
  const { t } = useTranslation();

  const severityDescription = (lampStatus = '') => {
    switch (lampStatus) {
      case 'Yellow':
        return { label: 'Warning', variant: 'warning-subtle' as const };
      case 'Red':
        return { label: 'Critical', variant: 'error-subtle' as const };
      case 'White':
        return { label: 'Information', variant: 'neutral-subtle' as const };
      default:
        return null;
    }
  };

  const columns = [
    {
      accessorFn: (row) => row.ChassisId,
      id: 'chassisId',
      header: t(($) => $.components.table.fault_codes_table.unit),
      minSize: 200,
      cell: (info) => {
        const row = info.row.original;
        const chassisId = info.getValue<string>();
        const unitName = units?.find((u) => chassis.getUnitsChassisIds(u).includes(chassisId))?.unitName || '';

        return (
          <div className="flex flex-col">
            <Typography variant="caption1" className="whitespace-nowrap">
              {row.NodeName} {chassisId}
            </Typography>
            <Typography variant={'caption2'} className="whitespace-nowrap">
              {unitName}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.IsoDtc,
      id: 'IsoDtc',
      header: t(($) => $.components.table.fault_codes_table.fault_code),
      minSize: 150,
      cell: (info) => {
        const faultCode = info.getValue<string>() ?? '-';
        return <Typography variant="caption1">{faultCode}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.LampStatus,
      id: 'severity',
      header: t(($) => $.components.table.fault_codes_table.severity),
      cell: (info) => {
        const lampStatus = info.getValue<ActiveFaultCodeItem['LampStatus']>();
        const description = severityDescription(lampStatus ?? undefined);
        if (!description) {
          return <Typography variant="caption1">-</Typography>;
        }
        return (
          <Tag size="small" variant={description.variant}>
            {description.label}
          </Tag>
        );
      },
    },
    {
      accessorFn: (row) => row.FailureTypeName,
      id: 'failureTypeName',
      header: t(($) => $.components.table.fault_codes_table.failure_event),
      minSize: 250,
      cell: (info) => {
        const failureTypeName = info.getValue<string>() ?? '-';
        return <Typography variant="caption1">{failureTypeName}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.DtcName,
      id: 'DtcName',
      header: t(($) => $.components.table.fault_codes_table.fault_description),
      minSize: 400,
      cell: (info) => {
        const description = info.getValue<string>() ?? '-';
        return <Typography variant="caption1">{description}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.LastFailureDateTime,
      id: 'lastFailureDateTime',
      header: t(($) => $.components.table.fault_codes_table.timestamp),
      cell: (info) => {
        const lastFailureDateTime = info.getValue<string>();
        return (
          <FormatDateTime date={lastFailureDateTime} type="datetime" variant="caption1" className="whitespace-nowrap" />
        );
      },
    },
    {
      accessorFn: (row) => row.LastFailureTotalOperatingTime_h,
      id: 'lastFailureTotalOperatingTime_h',
      header: t(($) => $.components.table.fault_codes_table.operating_hours),
      cell: (info) => {
        const operatingHours = info.getValue<number | undefined>();
        if (operatingHours === undefined || operatingHours === null || isNaN(operatingHours) || operatingHours <= 0) {
          return <Typography variant="caption1">-</Typography>;
        }
        return (
          <Typography variant="caption1">
            {operatingHours?.toLocaleString(undefined, { maximumFractionDigits: 0 })} h
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.OccurrenceCounter,
      id: 'occurrenceCounter',
      header: t(($) => $.components.table.fault_codes_table.occurrences),
      cell: (info) => {
        const occurrences = info.getValue<string>() ?? '-';
        return <Typography variant="caption1">{occurrences}</Typography>;
      },
    },
  ] as ColumnDef<ActiveFaultCodeItem>[];

  return { columns };
};
export default useFaultCodesTable;
